import { SnakeToCamel } from 'utils/caseTransformers';

/*************************
 *    Constants
 ************************/
export const GRANULARITY = {
  DAY: 'day',
  MONTH: 'month',
} as const;

export type Granularity = (typeof GRANULARITY)[keyof typeof GRANULARITY];

export const ANALYTICS_METRIC = {
  CREDITS: 'credits',
  PRS: 'prs',
  REVIEWS: 'reviews',
} as const;

export type AnalyticsMetric =
  (typeof ANALYTICS_METRIC)[keyof typeof ANALYTICS_METRIC];

/*************************
 *    Snaked case API
 ************************/
export interface SnakedBillingQuotaBalance {
  credits: {
    current: number;
    maximum: number;
    renewal_date: string;
  } | null;
  usage: {
    average_daily_spend: number;
  };
  cycle_anchor_day: number | null;
  is_overage: boolean;
  overage_amount: number;
  is_trial?: boolean;
  credits_used?: number;
  review_count: number;
  pr_count: number;
  review_count_ytd: number;
  pr_count_ytd: number;
  ytd_only?: boolean;
  cycle_start: string | null;
  cycle_end: string | null;
}

export interface SnakedAnalyticsCycleSegment {
  cycle_id: string;
  cycle_start: string;
  cycle_end: string;
  label: string;
  credits: number;
  within_pool: number;
  overage: number;
  overage_began?: string | null;
}

export interface SnakedAnalyticsBucket {
  period: string;
  period_label: string;
  total: number;
  within_pool?: number | null;
  overage?: number | null;
  cycle_segments?: SnakedAnalyticsCycleSegment[] | null;
}

export interface SnakedCycleBoundaryMarker {
  date: string;
  label: string;
  from_cycle_id?: string | null;
  to_cycle_id?: string | null;
}

export interface SnakedAnalyticsResponse {
  metric: AnalyticsMetric;
  granularity: Granularity;
  buckets: SnakedAnalyticsBucket[];
  cycle_boundaries: SnakedCycleBoundaryMarker[];
}

/*************************
 *         GET
 *************************/
export type GetBillingQuotaBalanceResponse =
  SnakeToCamel<SnakedBillingQuotaBalance>;

export type GetBillingQuotaBalanceParams = Record<string, never>;

export interface GetBillingAnalyticsParams {
  start_date: string;
  end_date: string;
  metric: AnalyticsMetric;
  granularity?: Granularity;
}

export type AnalyticsCycleSegment = SnakeToCamel<SnakedAnalyticsCycleSegment>;
export type AnalyticsBucket = SnakeToCamel<SnakedAnalyticsBucket>;
export type CycleBoundaryMarker = SnakeToCamel<SnakedCycleBoundaryMarker>;
export type GetBillingAnalyticsResponse = SnakeToCamel<SnakedAnalyticsResponse>;

/*************************
 *   Reviews
 ************************/
export interface SnakedBillingReview {
  request_id: string;
  date: string;
  credits: number;
  pr_number: string | null;
  review_type: string;
  complexity: string;
  // Surfaces on BillingReview as `reviewMode` (SnakeToCamel). Optional/nullable:
  // absent on older responses and until the companion backend ships it.
  review_mode?: string | null;
}

export interface SnakedBillingReviewsResponse {
  reviews: SnakedBillingReview[];
  next_cursor?: string | null;
  has_more?: boolean;
  is_truncated?: boolean;
}

export type BillingReview = SnakeToCamel<SnakedBillingReview>;

export type GetBillingReviewsResponse =
  SnakeToCamel<SnakedBillingReviewsResponse>;

export interface GetBillingReviewsParams {
  start_date: string;
  end_date: string;
  cursor?: string;
  limit?: number;
  search?: string;
}

/*************************
 *   Checkout (PLT-3715, PLT-4162)
 ************************/
// Body for POST /billing/v1/checkout/session. The BE is a CamelModel, so the
// wire shape is already camelCase (no SnakeToCamel adaptation needed). planSku
// is the requested monthly credit count as a string (e.g. "5000"); the BE
// resolves it to a Stripe price off the live self-serve ladder (PLT-4163).
export interface CreateCheckoutSessionRequest {
  planSku: string;
}

// Response from POST /billing/v1/checkout/session. `url` is the Stripe-hosted
// Checkout link the FE redirects the user to; `sessionId` is the cs_... id.
export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// One rung of the self-serve plan ladder, from GET /billing/v1/checkout/packages
// (PLT-4162). Sourced live from Stripe; no price_id is exposed — `credits` is
// the identity the FE sends back as planSku. CamelModel BE → already camelCase.
export interface CheckoutPackage {
  credits: number;
  monthlyUsd: number;
}

// Response from GET /billing/v1/checkout/packages: the plan ladder for the
// conversion-popup carousel, smallest credits first.
export interface GetCheckoutPackagesResponse {
  packages: CheckoutPackage[];
}

// Body for POST /billing/v1/conversions/move (the paid-cohort "Move"). Like
// checkout, the FE sends only `planSku` (credits as a string); the BE resolves
// it to a Stripe price id server-side — no price id is ever exposed to the FE.
export interface MigrateConversionRequest {
  planSku: string;
}

// Response from POST /billing/v1/conversions/move (and /move/reverse): the
// conversion record's post-Move view (CamelModel BE → already camelCase). `state`
// is the terminal / parked state the caller inspects — `refunded` is silent
// success; `reversed` is the reversal success; `aborted` / `held` mean the Move
// didn't complete cleanly.
export interface MigrateConversionResponse {
  id: string | null;
  accountId: string;
  cohort: string;
  state: string;
  status: string;
  nextSweepAt: string | null;
  heldReason: string | null;
  // True only on the 3DS/SCA path: the off-session charge needs card
  // authentication, so the BE parked the Move (state `payment_action_required`).
  // The FE must ask the user to confirm the reversal (cancel + refund → standard
  // checkout) rather than treating the Move as done.
  paymentActionRequired: boolean;
}

// The Move succeeded silently (off-session charge cleared) when the record
// reaches this state. The endpoint returns 200 for aborted / held outcomes too,
// so callers must check `state` before treating the Move as done.
export const MOVE_SUCCESS_STATE = 'refunded';

// /move/reverse resolves here: the legacy sub was canceled + refunded and the
// account stamped self-serve, so the FE can hand off to a standard checkout.
export const MOVE_REVERSED_STATE = 'reversed';

/*************************
 *   Billing Portal (PLT-4174)
 ************************/
export interface CreateBillingPortalSessionResponse {
  url: string;
}

export const SUBSCRIPTION_STATE = {
  NONE: 'none',
  ACTIVE: 'active',
  SCHEDULED_TO_CANCEL: 'scheduledToCancel',
  // Downgrade scheduled at period end via a Stripe Subscription Schedule
  // (PLT-4468): the user keeps the current pool until renewal, then drops to
  // the smaller pack named by scheduledChange on GetBillingInfoResponse.
  SCHEDULED_TO_CHANGE: 'scheduledToChange',
  CANCELED: 'canceled',
  PAST_DUE: 'pastDue',
} as const;

export type SubscriptionState =
  (typeof SUBSCRIPTION_STATE)[keyof typeof SUBSCRIPTION_STATE];

export const OVERAGE_SOURCE = {
  USER: 'user',
  SUBSCRIPTION: 'subscription',
  NONE: 'none',
} as const;

export type OverageSource =
  (typeof OVERAGE_SOURCE)[keyof typeof OVERAGE_SOURCE];

// The account's overage cap from GET /billing-info. `chargeLimit` is the USD
// ceiling shown to the user and `skuAmount` its credit equivalent (same units
// as planSku). `source === 'none'` means not configured (both values are -1,
// which is not the same as unlimited).
export interface OverageInfo {
  chargeLimit: number;
  skuAmount: number;
  source: OverageSource;
}

// A scheduled plan change (downgrade) from GET /billing-info (PLT-4468).
// Present only while subscriptionState === 'scheduledToChange'. targetProductId
// is the smaller pack the subscription drops to; effectiveAt is the period-end
// date the change takes effect (ISO-8601).
export interface ScheduledChange {
  targetProductId: string;
  effectiveAt: string;
}

export interface GetBillingInfoResponse {
  subscriptionState: SubscriptionState;
  planSku: string | null;
  planName: string | null;
  monthlyUsd: number | null;
  cancelAt: string | null;
  scheduledChange?: ScheduledChange | null;
  overage?: OverageInfo;
}

// Body for PATCH /billing-info (admin only). snake_case, single field
// (extra=forbid on the server); the credit comparator is derived server-side.
// The response is the resolved OverageInfo block (camelCase).
export interface UpdateBillingInfoRequest {
  overage_charge_limit: number;
}

export interface CancelSubscriptionResponse {
  subscriptionState: SubscriptionState;
}
